import { orderBy } from 'lodash-es'
import type { LoanProviderPositionOverride } from 'loans/types/LoanProviderItem'
import { formatItems } from './utils'
const state = ref({
  loanAmount: 0,
  repayment: 0,
  termMonthsCount: {
    countYear: 0, countMonths: 0,
  },
  interest: 0,
  extendedNetIncome: 0,
  extendedMonthlyExpenses: 0,
  extendedMonthlyInstallment: 0,
  offers: [],
  activeTab: 'all',
  sort: 'recommended',
  activeFilters: [],
  filterTranslations: {},
})

function setTermMonthsCount(count: { countYear: number; countMonths: number }) {
  state.value.termMonthsCount = count
}
function setRepayment(repayment: number) {
  state.value.repayment = repayment
}
function setLoanAmount(amount: number) {
  state.value.loanAmount = amount
}
function setInterest(interest: number) {
  state.value.interest = interest
}
function setExtendedNetIncome(income: number) {
  state.value.extendedNetIncome = income
}
function setExtendedMonthlyExpenses(expenses: number) {
  state.value.extendedMonthlyExpenses = expenses
}
function setExtendedMonthlyInstallment(installment: number) {
  state.value.extendedMonthlyInstallment = installment
}
function setOffers(offers: any) {
  state.value.offers = offers
}
function setActiveTab(tab: any) {
  state.value.activeTab = tab
}
function setSort(option: string) {
  state.value.sort = option
}
function setActiveFilters(filters: any) {
  state.value.activeFilters = filters
}
function setFilterTranslations(translations: object) {
  state.value.filterTranslations = translations
}
function setOffersOverrides(overrides: Array<LoanProviderPositionOverride>) {
  state.value.offers = state.value.offers.map((offer) => {
    const override = overrides.find(override => override.provider === offer.uuid)
    if (override) {
      offer.content.loanCustomOrder = override.position
      if (override.promotionText)
        offer.content.sticker = override.promotionText
    }
    return offer
  })
}

function getFilteredLoanItems(sort = true, specificFilter?: string) {
  let items = []

  // filter
  items = formatItems(state.value.offers)
  items.forEach((item) => {
    let missingFilters = []
    if (state.value.activeTab !== 'all' && !(item.content.tags as Array<any>).find(tag => (state.value.activeTab as unknown as Array<any>).find(n => n === tag)))
      missingFilters.push((state.value.filterTranslations as any).loanType)

    if (state.value.loanAmount && Number(item.content.loanAmountMax) < state.value.loanAmount)
      missingFilters.push((state.value.filterTranslations as any).payment)

    if (state.value.activeFilters.length || specificFilter) {
      const filters = [...state.value.activeFilters]
      if (specificFilter)
        filters.push((specificFilter as never))

      const missingCheckFilters = filters.filter(a => !item.content.filterCriteria || !item.content.filterCriteria.includes(a))
      if (missingCheckFilters) {
        // translate check values to labels
        const translatedFilters = missingCheckFilters.map(filter => state.value.filterTranslations[filter])
        missingFilters = [...missingFilters, ...translatedFilters]
      }
    }

    item.content.missingFilters = missingFilters
  })

  // sort
  if (sort) {
    switch (state.value.sort) {
      case 'recommended':
        items = orderBy(items, ['content.loanCustomOrder', i => Number(i.content.interest.replace(',', '.')), i => Number(i.content.rating.replace(',', '.'))], ['desc', 'asc', 'desc'])
        break
      case 'interestAsc':
        items = orderBy(items, [i => Number(i.content.interest.replace(',', '.')), 'content.loanCustomOrder', i => Number(i.content.rating.replace(',', '.'))], ['asc', 'desc', 'desc'])
        break
      case 'interestDesc':
        items = orderBy(items, [i => Number(i.content.interest.replace(',', '.')), i => Number(i.content.rating.replace(',', '.'))], ['desc', 'desc'])
        break
      case 'loanInstalmentAsc':
        items = orderBy(items, [i => Number(i.content.interest.replace(',', '.')), 'content.loanCustomOrder', i => Number(i.content.rating.replace(',', '.'))], ['asc', 'desc', 'desc'])
        break
      case 'loanInstalmentDesc':
        items = orderBy(items, [i => Number(i.content.interest.replace(',', '.')), i => Number(i.content.rating.replace(',', '.'))], ['desc', 'desc'])
        break
      case 'ratingAsc':
        items = orderBy(items, i => Number(i.content.rating.replace(',', '.')))
        break
      case 'ratingDesc':
        items = orderBy(items, i => Number(i.content.rating.replace(',', '.')), 'desc')
        break
    }
    // add items with missingFilters to end
    items = orderBy(items, 'content.missingFilters')
  }

  return items
}

function getInterest() {
  return Math.ceil(
    state.value.loanAmount >= 20000
      ? state.value.interest
      : state.value.loanAmount >= 10000
        ? state.value.interest * 1.42
        : state.value.loanAmount >= 5000
          ? state.value.interest * 1.71
          : state.value.interest * 2.14,
  )
}

function getTermMonths() {
  return state.value.termMonthsCount.countYear * 12 + state.value.termMonthsCount.countMonths
}

function getUrlQueryParams() {
  if (state.value.activeFilters.length)
    return `?${state.value.activeFilters.join('=true&')}=true&loanAmount=${state.value.loanAmount}&maxRepayment=${state.value.repayment}`
  else
    return `?loanAmount=${state.value.loanAmount}&maxRepayment=${state.value.repayment}`
}

function setActiveFiltersFromQuery(query: string): string {
  const pairs = query.substring(1).split('&')

  const truePairs = pairs.filter((pair) => {
    const [_, value] = pair.split('=')
    return value === 'true'
  })
  const filters = truePairs.map(pair => pair.split('=')[0]) as any
  state.value.activeFilters = filters

  return filters
}

export function useLoanState() {
  return {
    state: readonly(state),
    setTermMonthsCount,
    setRepayment,
    setLoanAmount,
    getTermMonths,
    setInterest,
    setExtendedNetIncome,
    setExtendedMonthlyExpenses,
    setExtendedMonthlyInstallment,
    getInterest,
    setOffers,
    setActiveTab,
    setSort,
    getFilteredLoanItems,
    setActiveFilters,
    getUrlQueryParams,
    setActiveFiltersFromQuery,
    setFilterTranslations,
    setOffersOverrides,
  }
}
